'use client'
import { useState, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLanguage } from '@/lib/context/LanguageContext'
import { PHONE_E164 } from '@/lib/i18n'
import { ZoomIn } from '@/components/ui/FadeInUp'
import SectionHeader from '@/components/ui/SectionHeader'
import { buildContactSchema, type ContactFormData } from '@/lib/schemas/contact'

type Status = 'idle' | 'submitting' | 'success' | 'error'

const inputCls =
  'w-full rounded-xl border border-ink/10 bg-white px-4 py-3 text-sm text-ink placeholder:text-muted hover:border-ink/20 focus:outline-none focus:border-accent/40 focus:ring-2 focus:ring-accent/10 transition-all'

const inputErrorCls = 'border-red-400 hover:border-red-500 focus:border-red-500 focus:ring-red-100'

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024
const ALLOWED_UPLOAD_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/webp',
])
const ACCEPT_ATTR = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.webp'

export default function ContactCTA() {
  const { t, lang } = useLanguage()
  const c = t.contact

  // Rebuild the schema when the language switches so error messages
  // come back in the active locale.
  const schema = useMemo(() => buildContactSchema(lang), [lang])

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', company: '', budget: '', message: '' },
    mode: 'onBlur',
  })

  const [status, setStatus] = useState<Status>('idle')

  // Attachment lives outside RHF — File objects don't survive the JSON path
  // we use for plain submissions, and Zod's File validation differs between
  // browser and node anyway. Client-side checks here mirror the server
  // limits (10 MB + MIME allowlist).
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [attachment, setAttachment] = useState<File | null>(null)
  const [attachmentError, setAttachmentError] = useState<string | null>(null)

  function onAttachmentChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null
    setAttachmentError(null)
    if (!file) {
      setAttachment(null)
      return
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setAttachmentError(c.errors.attachmentTooLarge)
      setAttachment(null)
      e.target.value = ''
      return
    }
    if (!ALLOWED_UPLOAD_TYPES.has(file.type)) {
      setAttachmentError(c.errors.attachmentTypeInvalid)
      setAttachment(null)
      e.target.value = ''
      return
    }
    setAttachment(file)
  }

  function clearAttachment() {
    setAttachment(null)
    setAttachmentError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  async function onSubmit(data: ContactFormData) {
    setStatus('submitting')
    try {
      let res: Response
      if (attachment) {
        const body = new FormData()
        body.append('name', data.name)
        body.append('email', data.email)
        body.append('company', data.company)
        if (data.budget) body.append('budget', data.budget)
        if (data.message) body.append('message', data.message)
        body.append('attachment', attachment)
        res = await fetch('/api/contact', { method: 'POST', body })
      } else {
        res = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        })
      }
      if (!res.ok) {
        setStatus('error')
        return
      }
      setStatus('success')
      reset()
      clearAttachment()
    } catch (e) {
      console.error('[ContactCTA] submission failed', e)
      setStatus('error')
    }
  }

  return (
    <section id="contact" className="py-16 md:py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader num="07" label={c.section} />

        <ZoomIn delay={0.08}>
          <div
            className="rounded-2xl border border-ink/8 overflow-hidden"
            style={{
              background:
                'linear-gradient(135deg, rgba(3,53,183,0.06), rgba(0,120,182,0.04) 50%, rgba(0,180,216,0.03))',
            }}
          >
            <div className="grid md:grid-cols-2 gap-0">
              {/* Left info */}
              <div className="p-6 sm:p-10 md:p-14 flex flex-col gap-6 md:gap-8 border-e border-ink/5">
                <h2 className="font-display font-black text-3xl md:text-4xl text-ink leading-snug">
                  {c.heading}
                </h2>
                <p className="text-muted text-sm leading-relaxed">{c.sub}</p>

                <div className="flex flex-col gap-4 text-sm pt-4 border-t border-ink/8">
                  <a
                    href={`mailto:${c.contactEmail}`}
                    className="flex items-center gap-3 text-muted hover:text-ink transition-colors"
                  >
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    {c.contactEmail}
                  </a>
                  <a
                    href={`tel:${PHONE_E164}`}
                    className="flex items-center gap-3 text-muted hover:text-ink transition-colors"
                  >
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    <span dir="ltr">{c.contactPhone}</span>
                  </a>
                  <span className="flex items-center gap-3 text-muted">
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    {c.address}
                  </span>
                </div>
              </div>

              {/* Right form */}
              {status === 'success' ? (
                <div className="p-6 sm:p-10 md:p-14 flex flex-col items-center justify-center gap-4 text-center">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg,#0335b7,#00b4d8)' }}
                  >
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="font-display font-black text-xl text-ink">{c.successTitle}</p>
                  <p className="text-muted text-sm leading-relaxed">{c.successSub}</p>
                </div>
              ) : (
                <form
                  // The submit handler reads fileInputRef inside clearAttachment(),
                  // but only when the form is actually submitted — not during render.
                  // eslint-disable-next-line react-hooks/refs
                  onSubmit={handleSubmit(onSubmit)}
                  className="p-6 sm:p-10 md:p-14 flex flex-col gap-4"
                  noValidate
                >
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="sr-only" htmlFor="name">
                        {c.name}
                      </label>
                      <input
                        id="name"
                        type="text"
                        placeholder={c.name}
                        aria-invalid={!!errors.name}
                        className={`${inputCls} ${errors.name ? inputErrorCls : ''}`}
                        {...register('name')}
                      />
                      {errors.name && (
                        <p className="text-red-500 text-xs mt-1.5" role="alert">
                          {errors.name.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="sr-only" htmlFor="email">
                        {c.email}
                      </label>
                      <input
                        id="email"
                        type="email"
                        placeholder={c.email}
                        aria-invalid={!!errors.email}
                        className={`${inputCls} ${errors.email ? inputErrorCls : ''}`}
                        {...register('email')}
                      />
                      {errors.email && (
                        <p className="text-red-500 text-xs mt-1.5" role="alert">
                          {errors.email.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="sr-only" htmlFor="company">
                      {c.company}
                    </label>
                    <input
                      id="company"
                      type="text"
                      placeholder={c.company}
                      aria-invalid={!!errors.company}
                      className={`${inputCls} ${errors.company ? inputErrorCls : ''}`}
                      {...register('company')}
                    />
                    {errors.company && (
                      <p className="text-red-500 text-xs mt-1.5" role="alert">
                        {errors.company.message}
                      </p>
                    )}
                  </div>

                  <div className="relative">
                    <label className="sr-only" htmlFor="budget">
                      {c.budget}
                    </label>
                    <select
                      id="budget"
                      aria-invalid={!!errors.budget}
                      className={`${inputCls} appearance-none pr-10 text-muted ${errors.budget ? inputErrorCls : ''}`}
                      {...register('budget')}
                    >
                      <option value="">{c.budget}</option>
                      {c.budgetOpts.map((o: string) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    {errors.budget && (
                      <p className="text-red-500 text-xs mt-1.5" role="alert">
                        {errors.budget.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="sr-only" htmlFor="message">
                      {c.message}
                    </label>
                    <textarea
                      id="message"
                      rows={4}
                      placeholder={c.message}
                      aria-invalid={!!errors.message}
                      className={`${inputCls} resize-none ${errors.message ? inputErrorCls : ''}`}
                      {...register('message')}
                    />
                    {errors.message && (
                      <p className="text-red-500 text-xs mt-1.5" role="alert">
                        {errors.message.message}
                      </p>
                    )}
                  </div>

                  {/* Optional attachment */}
                  <div>
                    <label
                      htmlFor="attachment"
                      className="flex items-center justify-between gap-3 cursor-pointer rounded-xl border border-dashed border-ink/15 bg-white/50 px-4 py-3 text-sm text-muted hover:border-accent/50 hover:bg-white transition-all"
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <svg
                          className="w-4 h-4 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                          />
                        </svg>
                        <span className="truncate">
                          {attachment ? attachment.name : c.attachmentLabel}
                        </span>
                      </span>
                      {attachment && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            clearAttachment()
                          }}
                          className="text-xs font-semibold text-ink/50 hover:text-red-500 transition-colors flex-shrink-0"
                        >
                          {c.attachmentRemove}
                        </button>
                      )}
                    </label>
                    <input
                      ref={fileInputRef}
                      id="attachment"
                      type="file"
                      accept={ACCEPT_ATTR}
                      onChange={onAttachmentChange}
                      className="sr-only"
                    />
                    {attachmentError && (
                      <p className="text-red-500 text-xs mt-1.5" role="alert">
                        {attachmentError}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3.5 rounded-xl font-semibold text-white text-sm transition-all duration-200 hover:opacity-90 hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                    style={{ background: 'linear-gradient(135deg,#0335b7,#00b4d8)' }}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        {c.submitting}
                      </span>
                    ) : (
                      c.submit
                    )}
                  </button>

                  {status === 'error' && (
                    <p className="text-red-500 text-xs text-center" role="alert">
                      {c.error}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </ZoomIn>
      </div>
    </section>
  )
}
